import React, { useEffect, useState } from "react";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { Button, Table, Row, Col, Form, Alert } from "react-bootstrap";

const Unit = () => {
  const [unitSettings, setUnitSettings] = useState([]);
  const [filteredSettings, setFilteredSettings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: "", variant: "" });
  const [pendingChanges, setPendingChanges] = useState({});

  // Fetch unit settings from backend
  const fetchUnitSettings = async () => {
    setLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getUnitSettings`,
        {},
        {},
        "GET"
      );

      if (isSuccess && data?.data) {
        setUnitSettings(data.data);
        setFilteredSettings(data.data);
      } else {
        showAlert("Failed to fetch unit settings", "danger");
      }
    } catch (error) {
      console.error("Error fetching unit settings:", error);
      showAlert("Error fetching unit settings", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Handle selected unit change
  const handleUnitChange = (settingId, newSelectedUnit) => {
    setPendingChanges(prev => ({
      ...prev,
      [settingId]: newSelectedUnit
    }));
  };

  // Save unit setting
  const handleSaveUnit = async (settingId) => {
    const newSelectedUnit = pendingChanges[settingId];
    
    if (!newSelectedUnit) {
      return;
    }

    setSavingId(settingId);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}saveUnitSetting`,
        {},
        {
          id: settingId,
          selectedUnit: newSelectedUnit,
        },
        "POST"
      );

      if (isSuccess) {
        showAlert(data?.message || "Unit updated successfully", "success");
        // Remove from pending changes
        setPendingChanges(prev => {
          const updated = { ...prev };
          delete updated[settingId];
          return updated;
        });
        fetchUnitSettings(); // Refresh the list
      } else {
        showAlert(data?.message || "Failed to update unit", "danger");
      }
    } catch (error) {
      console.error("Error saving unit:", error);
      showAlert("Error saving unit", "danger");
    } finally {
      setSavingId(null);
    }
  };

  // Show alert message
  const showAlert = (message, variant) => {
    setAlertInfo({ show: true, message, variant });
    setTimeout(() => {
      setAlertInfo({ show: false, message: "", variant: "" });
    }, 3000);
  };

  // Search functionality on category and subcategory
  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredSettings(unitSettings);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = unitSettings.filter(
      (setting) =>
        setting.category.toLowerCase().includes(searchLower) ||
        setting.subCategory.toLowerCase().includes(searchLower)
    );
    setFilteredSettings(filtered);
  };

  useEffect(() => {
    fetchUnitSettings();
  }, []);

  return (
    <div style={{ width: "100%" }}>
      <div className="color_div_on framwork_2 pt-0">
        <div className="business_detail">
          {alertInfo.show && (
            <Alert 
              variant={alertInfo.variant} 
              onClose={() => setAlertInfo({ show: false, message: "", variant: "" })} 
              dismissible
            >
              {alertInfo.message}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <h4>Unit Settings</h4>
            </Col>
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Search by category or subcategory..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </Col>
          </Row>

          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th>Selected Unit</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      Loading...
                    </td>
                  </tr>
                ) : filteredSettings && filteredSettings.length > 0 ? (
                  filteredSettings.map((setting, index) => (
                    <tr key={setting.id}>
                      <td>{index + 1}</td>
                      <td>{setting.category}</td>
                      <td>{setting.subCategory}</td>
                      <td>
                        <Form.Control
                          as="select"
                          value={pendingChanges[setting.id] !== undefined ? pendingChanges[setting.id] : (setting.selectedUnit || setting.baseUnit)}
                          onChange={(e) => handleUnitChange(setting.id, e.target.value)}
                          disabled={setting.isEditable === false || !setting.status}
                          style={{ minWidth: "150px" }}
                        >
                          {setting.supportedUnits?.map((unitObj, idx) => (
                            <option key={idx} value={unitObj.unit}>
                              {unitObj.unit}
                            </option>
                          ))}
                        </Form.Control>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSaveUnit(setting.id)}
                          disabled={
                            setting.isEditable === false || 
                            !setting.status || 
                            pendingChanges[setting.id] === undefined ||
                            savingId === setting.id
                          }
                          title="Save Unit"
                        >
                          {savingId === setting.id ? (
                            <>
                              <i className="fa fa-spinner fa-spin"></i> Saving...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-save"></i> Save
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      {searchTerm ? "No matching records found" : "No data available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unit;